import { Transform, Type } from "class-transformer";
import Security from "./fixProtocol/instrument/security";
import PortfolioComponent, { COMPONENT_TYPES } from "./portfolioComponent";
import RebalanceItem from "./rebalanceItem";
import DynamicWeight from "./dynamicWeight";

export default class Position extends PortfolioComponent {

  @Transform(({ value }) => value && value.toISOString(), { toPlainOnly: true })
  @Transform(({ value }) => value && new Date(value), { toClassOnly: true })
  priceDate: Date;

  @Transform(({ value }) => value && value.toISOString(), { toPlainOnly: true })
  @Transform(({ value }) => value && new Date(value), { toClassOnly: true })
  lastUpdateTime: Date;

  /** Security (also known as asset) of this position. */
  @Type(() => Security)
  security?: Security;

  /** Weight dynamically determined by the position price. */
  @Type(() => DynamicWeight)
  dynamicWeight?: DynamicWeight;

  /**
   * @param id GUID of this position.
   * @param portfolioId ID of the portfolio to which this portfolio component belongs to.
   * @param security Security (also known as asset) of this position.
   * @param Description 
   * @param quantity Quantity of the security hold by this position.
   * @param price Price at which this position is being valued.
   * @param priceDate Date of reference of the price of this position.
   * @param lastUpdateTime Last time this position was updated.
   * @param weight Relative weight to be given to this portfolio during rebalancing.
   * @param parentCompositeId ID of parent portfolio composite. If undefined, this component is the root composite.
   * @param dynamicWeight Weight dynamically determined by the position price. If set, this property will overwrite the given weight.
   */
  constructor(
    public id: string | undefined,
    public portfolioId: string,
    security: Security | undefined,
    public description: string,
    public quantity: number,
    private _price: number,
    priceDate: Date,
    lastUpdateTime: Date,
    public weight: number,
    public parentCompositeId?: string,
    dynamicWeight?: DynamicWeight,
  ) {
    super();
    this.componentType = COMPONENT_TYPES.POSITION;
    this.security = security;
    this.priceDate = priceDate;
    this.lastUpdateTime = lastUpdateTime;
    this.dynamicWeight = dynamicWeight;
    this.price = _price;
  }

  public get price() {
    return this._price;
  }

  public set price(thePrice: number) {
    this._price = thePrice;
    this.setDynamicWeight();
  }

  notional(): number {
    return this.quantity * this.price;
  }

  /**
   * Sets the ideal notional value that this portfolio component should have.
   * @param totalPortfolioNotional 
   */
  setTotalPortfoliolNotional(totalPortfolioNotional: number): void {
    this.totalPortfolioNotional = totalPortfolioNotional;
  }

  /**
   * Sets the ideal notional value that this portfolio component should have.
   * @param idealNotional 
   */
  setIdealNotional(idealNotional: number): void {
    this._idealNotional = idealNotional;
  }

  /**
   * Sets the weight of positions with dynamic weight.
   */
  setDynamicWeight(): void {
    if (this.dynamicWeight && this.dynamicWeight.weightPriceRanges.length > 0) {
      this.weight = this.dynamicWeight.weight(this);
    }
  }

  clone(): PortfolioComponent {
    // Important: keep the clone quantity at 0 (zero) in order to prevent sensitive information (portfolio NAV) leakage when cloning his position as a PortfolioComponent.
    let thisZeroQuantityIsimportant: number = 0;
    return new Position(
      undefined,
      this.portfolioId,
      this.security,
      this.description,
      thisZeroQuantityIsimportant,
      this.price,
      this.priceDate,
      this.lastUpdateTime,
      this.weight);
  }

  updateComponent(component: PortfolioComponent): boolean {
    if ((component instanceof Position) && this.id === component.id) {
      Object.assign(this, component);
      return true;
    } else {
      return false;
    }
  }

  /**
   * Returns the necessary item to rebalance this position.
   * @param newInvestment The notional value of new money to be invested, defaults to 0.
   * @param allowFractionQuantity Informs if fractionary quantities are allowed. e.g. brazilian stocks in generall allow 100 quantity buy/sell, but fractionary allows for 1.
   * @returns 
   */
  calculateRebalanceItem(newInvestment: number = 0, allowFractionQuantity: boolean): RebalanceItem {
    const theoreticalQuantity = (newInvestment * this.idealPercentage() + this.idealNotional() - this.notional()) / this.price;
    const quantityStep = (() => {
      switch (this.security?.assetType?.assetSubclass.assetClass.name) {
        case 'Equity':
          return allowFractionQuantity ? 1 : 100;
        case 'Fund':
          return 1e-8; // 8 decimal places for investment funds quantity
        case 'Debt':
          return 1e-2; // 2 decimal places for bonds
        default:
          return 1;
      }
    })();
    const quantity = Math.sign(theoreticalQuantity) * Math.round(Math.abs(theoreticalQuantity / quantityStep)) * quantityStep;
    const price: number = this.price;
    const item: RebalanceItem = new RebalanceItem(
      undefined,
      '',
      quantity,
      price,
      false,
      new Date(),
      this
    );
    return item;
  }
}